import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Button } from '@givebutter/primitives/button';
import { faArrowDownToLine, faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchExportHistory } from 'api/contacts/exports';
import * as Table from 'components/table';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Export } from 'types/domain';

const columnHelper = createColumnHelper<Export>();

const ReportHistoryTable = () => {
    const { accountId } = useCurrentAccount();
    const { pagination, setPagination } = Table.useReactTableState({ pageSize: 5 });

    const { data: reportsHistory } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactsWithFilters(accountId, {
            page: pagination.pageIndex + 1,
        }),
        queryFn: () =>
            fetchExportHistory(accountId, {
                page: pagination.pageIndex + 1,
                limit: pagination.pageSize,
                template: 'contact.summary',
                type: 'zip',
            }),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Report Name',
                cell: info => {
                    return <div className={'font-medium'}>{info.getValue()}</div>;
                },
            }),
            columnHelper.accessor('created_at', {
                header: 'Date Generated',
                cell: info => <div>{info.getValue()}</div>,
            }),
            columnHelper.accessor('download_url', {
                header: '',
                id: 'download_url',
                cell: info => {
                    const value = info.getValue();
                    const isEnabled = !!info.row.original.completed_at;

                    return (
                        <Button
                            variant="none"
                            color="neutral"
                            aria-label={isEnabled ? 'Download' : 'Processing Report'}
                            onClick={() => window.open(value, '_blank')}
                            disabled={!isEnabled}
                        >
                            {isEnabled ? (
                                <FontAwesomeIcon icon={faArrowDownToLine} />
                            ) : (
                                <FontAwesomeIcon icon={faClock} />
                            )}
                        </Button>
                    );
                },
                meta: {
                    className: 'px-1 w-8',
                },
            }),
        ],
        [],
    );

    const table = useReactTable({
        data: reportsHistory.data || [],
        getCoreRowModel: getCoreRowModel(),
        columns,
        pageCount: reportsHistory.meta?.last_page,
        manualPagination: true,
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <Table.Root table={table} variant="bordered">
            <Table.Content>
                <Table.Header />
                <Table.Body />
            </Table.Content>
            <Table.Pagination>
                <Table.PaginationPageNavigation />
            </Table.Pagination>
            <Table.Empty variant={'secondary'}>
                <Table.EmptyIcon />
                <Table.EmptyTitle>There are no generated reports</Table.EmptyTitle>
            </Table.Empty>
        </Table.Root>
    );
};

export { ReportHistoryTable };
